import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card } from '../components/ui.tsx';

const FEATURES = [
  {
    title: 'Coaching, not just statistics',
    body:
      'Every insight names the metric it is based on, how you compare, and what to do about it. ' +
      'Nothing is reported without the sample size and confidence behind it.',
  },
  {
    title: 'Role-aware analysis',
    body:
      'Your position is inferred from lane and farm priority, then you are measured against the ' +
      'right yardstick — a hard support is never judged on gold per minute.',
  },
  {
    title: 'League leaderboards that hold up',
    body:
      'Import any league by ID and rank every player against others at their own position, with ' +
      'small samples regressed and opponent strength accounted for.',
  },
];

export function HomePage() {
  const [accountId, setAccountId] = useState('');
  const navigate = useNavigate();

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const match = /(\d{4,})/.exec(accountId.trim());
    if (match?.[1]) void navigate(`/players/${match[1]}`);
  };

  return (
    <div className="space-y-10">
      <section className="pt-6 text-center">
        <h1 className="text-3xl font-semibold tracking-tight text-ink-0 sm:text-4xl">
          Find out what is actually costing you rank.
        </h1>
        <p className="mx-auto mt-3 max-w-2xl text-sm text-ink-1">
          Import your last 100 matches, get benchmark-relative analysis of your play, and a ranked
          list of the highest-leverage things to fix.
        </p>

        <form onSubmit={submit} className="mx-auto mt-6 flex max-w-lg items-center gap-2">
          <input
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            placeholder="Enter your Dota account ID"
            aria-label="Dota account ID"
            className="flex-1 rounded-lg border border-border-subtle bg-surface-2 px-4 py-2.5 text-sm text-ink-0 placeholder:text-ink-2 focus:border-accent focus:outline-none"
          />
          <Button type="submit">Analyse</Button>
        </form>

        <p className="mt-3 text-xs text-ink-2">
          Or{' '}
          <a href="/api/auth/steam/login" className="text-accent hover:underline">
            sign in with Steam
          </a>{' '}
          to load your own profile automatically.
        </p>
      </section>

      <section className="grid gap-4 sm:grid-cols-3">
        {FEATURES.map((feature) => (
          <Card key={feature.title}>
            <h3 className="text-sm font-semibold text-ink-0">{feature.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-ink-1">{feature.body}</p>
          </Card>
        ))}
      </section>

      <Card
        title="Analysing a league"
        subtitle="Rank players by position across a whole tournament"
      >
        <p className="text-sm text-ink-1">
          Import any league by its ID to build positional leaderboards, team form, and hero contest
          rates. Ratings are explained metric by metric, so you can see exactly why a player ranks
          where they do.
        </p>
        <div className="mt-4">
          <Link to="/leagues" className="text-sm font-medium text-accent hover:underline">
            Go to leagues →
          </Link>
        </div>
      </Card>
    </div>
  );
}
